<template>
  <div class="option-img-btn question-img-btn">
    <div
      class="input-group-prepend pointer"
      @click="openOptionUploadImage(slideOption, index)"
    >
      <div
        class="input-group-text horizontal layout center center-justified question-img-btn-box"
        :class="{ 'btn-box-edit': slideOption.imageCropped }"
      >
        <img :src="slideOption.imageCropped" alt v-if="slideOption.imageCropped" />
        <i class="far fa-image" v-if="!slideOption.imageCropped" />
        <i class="fas fa-pencil-alt" />
      </div>
    </div>

    <div
      class="question-img-tooltip"
      v-if="slideOption.id && slideOption.id == isSlideOptionEditImageTooltipVisible"
      v-click-outside="closeEditDeleteSlideOptionImage"
    >
      <div class="tooltip-box vertical layout center center-justified">
        <aha-button
          class="mb-2"
          size="sm"
          id="question-image-edit-btn"
          @click="handleEditImageOptionButtonClick(slideOption)"
        >
          Edit
        </aha-button>
        <aha-button
          variant="danger"
          size="sm"
          id="question-image-delete-btn"
          @click="deleteOptionUploadedImage(slideOption, index)"
        >
          Delete
        </aha-button>
      </div>
      <div class="tooltip-arrow" />
    </div>

    <UploadImageV2
      v-if="isShowUploadModal"
      :type="'optionImage'"
      @hideModal="_hideModalUploadImage"
      @callback="handleUploadImageForm"
      :updateGifInBackground="handleUpdateGifInBackground"
      @selectExistImage="selectExistImage"
    />

    <CropImageV2
      v-if="isCropImageModalShowing"
      :image-url="imageUrl"
      :cropper-data="cropperData"
      :aspect-ratio="1"
      @hideModal="_closeModalCrop"
      @on-cancel="handleCancelCrop"
      :updateInBackground="handleUpdateInBackground"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import UploadImageV2 from '@/components/presentations/UploadImage.v2.vue'
import CropImageV2 from '@/components/presentations/CropImage.v2.vue'

import utils from '@/util'
import Config from '@/config'
import Socket from '@/socket'
import EventBus from '@/eventBus'
import SlideOptionApi from '@/api/slideOption'
import BackgroundUploadMixin from '@/mixins/slide/BackgroundUploadMixin'
import {
  removeObjectFromIndexedDB,
  setObjectToIndexedDB,
  Stores,
} from '@/utils/indexedDB/indexed.db'

export default {
  components: {
    UploadImageV2,
    CropImageV2,
  },
  mixins: [BackgroundUploadMixin],
  props: {
    slideOption: {
      type: Object,
      default: null,
    },
    index: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      dataConfirm: {},
      isUploading: false,
      isSlideOptionEditImageTooltipVisible: null,
      isCropImageModalShowing: false,
      imageUrl: '',
      selectedImageOption: {},
      isLoadingOpenOptionImage: false,
      isShowUploadModal: false,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    ...mapGetters(['isPresenting', 'getSlideOptionsActive']),

    openOptionUploadImage(option, index) {
      this.selectedImageOption = option
      if (!option.id) {
        if (this.isLoadingOpenOptionImage) return
        this.isLoadingOpenOptionImage = true
        this.isShowUploadModal = true
        this.isLoadingOpenOptionImage = false
        this.updateOption(option, index, (createdOptionId) => {
          option.id = createdOptionId
        })
      } else if (!option.imageCropped) {
        this.isShowUploadModal = true
      } else {
        this.isSlideOptionEditImageTooltipVisible = option.id
      }
    },

    async updateOption(option, index, cb) {
      this.setSyncLoadingAction(1)
      const { SlideOptions: slideOptions } = this.slideActive

      const highestOrder = slideOptions.reduce((accumulator, item) => {
        return item.order > accumulator ? item.order : accumulator
      }, 1)

      if (slideOptions[index].isLoading) return
      slideOptions[index].isLoading = true
      const payload = {
        title: option.value,
        order: slideOptions[index].order || highestOrder + 1,
        slideId: this.slideActive.id,
        presentationId: this.presentation.id,
        accessCode: this.presentation.uniqueAccessCode,
        correct: option.correct,
        socketId: Socket.getSocketId(),
      }
      try {
        if (index === undefined) return

        if (this.slideActive.SlideOptions[index]) {
          slideOptions[index].title = payload.title
          slideOptions[index].order = payload.order
        }

        this.updateSlideActiveAction({
          SlideOptions: slideOptions,
        })

        const slideId = this.slideActive.id
        const res = await SlideOptionApi.createSlideOption(payload)
        if (slideOptions[index]) {
          slideOptions[index] = res
        }
        if (cb) {
          cb(res.id)
        }

        if (slideId !== this.slideActive.id) {
          const slideIndex = this.presentation.slides.findIndex((item) => {
            return item.id === this.slideActive.id
          })
          if (slideIndex !== -1) {
            this.presentation.slides[slideIndex].SlideOptions = slideOptions
          }
        }
        this.setSyncLoadingAction(2)
        utils.gaTrackEvent('Presentation Editor', 'Slide option > Edit > Succeeded')
        EventBus.$emit('changeSlideOption')
      } catch {
        this.setSyncLoadingAction(3)
      }
    },

    handleEditImageOptionButtonClick(slideOption) {
      this.imageUrl = slideOption.image || slideOption.imageCropped
      this.cropperData = slideOption.lastCropperData

      if (slideOption.lastCropperData) {
        this.isCropImageModalShowing = true
      } else {
        this.isShowUploadModal = true
      }
    },

    closeEditDeleteSlideOptionImage() {
      if (!this.isSlideOptionEditImageTooltipVisible) return
      this.isSlideOptionEditImageTooltipVisible = null
    },

    deleteOptionUploadedImage(option, index) {
      const { SlideOptions } = this.slideActive
      SlideOptions[index].image = null
      SlideOptions[index].imageCropped = null
      this.updateSlideActiveAction({
        SlideOptions,
      })
      this.selectedImageOption = {}
      this.isSlideOptionEditImageTooltipVisible = null
      removeObjectFromIndexedDB(option.id, Stores.slideOptionImageStore)
      this._updateOption(
        {
          image: null,
          imageCropped: null,
          presentationId: this.$store.state.presentation.id,
          accessCode: this.$store.state.presentation.uniqueAccessCode,
        },
        option.id,
      )
    },

    _hideModalUploadImage() {
      this.isShowUploadModal = false
    },

    async handleUploadImageForm(imageUrl) {
      const [originImageUrl] = imageUrl.split('?')
      const extension = originImageUrl.split('.').pop()

      if (extension === 'gif') {
        // Gif image will be handled in the updateGifInBackground()

        return
      }

      this.imageUrl = imageUrl
      this.cropperData = null
      this.isShowUploadModal = false
      this.isCropImageModalShowing = true
    },

    getThumbnail(item) {
      return item.name.split('.').pop() === 'gif'
        ? item.origin
        : item.thumbnail
    },

    async selectExistImage(obj) {
      const thumbnail = this.getThumbnail(obj)

      const updatedOption = {
        imageCropped: thumbnail,
        imageExtension: null,
        lastCropperData: null,
        image: thumbnail,
      }

      const newSlideOptions = [...this.slideActive.SlideOptions]
      newSlideOptions[this.index] = Object.assign(this.slideOption, updatedOption)

      this.updateSlideActiveAction({
        SlideOptions: newSlideOptions,
      })

      const payload = {
        image: thumbnail,
        imageCropped: thumbnail,
        presentationId: this.presentation.id,
        accessCode: this.presentation.uniqueAccessCode,
        socketId: Socket.getSocketId(),
      }

      // api update
      try {
        await SlideOptionApi.updateSlideOption(this.slideOption.id, payload)
      } finally {
        this.isShowUploadModal = false
        EventBus.$emit('selectedMyPhotoItem')
      }
    },

    _closeModalCrop() {
      this.isCropImageModalShowing = false
    },

    handleCancelCrop() {
      this.isCropImageModalShowing = false
      this.isShowUploadModal = true
    },

    updateSlideOptionImageLocally({
      blob, cropperData, extension, originImage, slideOption,
    }) {
      const url = URL.createObjectURL(blob)

      const updatedOption = {
        imageCropped: url,
        imageExtension: extension,
        lastCropperData: cropperData,
        image: originImage,
      }
      slideOption = Object.assign(slideOption, updatedOption)

      const newSlideOptions = [...this.slideActive.SlideOptions]
      newSlideOptions[this.index] = slideOption

      this.updateSlideActiveAction({
        SlideOptions: newSlideOptions,
      })

      const payload = {
        blob,
        lastCropperData: cropperData,
        originImage,
        extension,
        slideId: this.slideActive.id,
      }
      setObjectToIndexedDB(this.slideOption.id, Stores.slideOptionImageStore, payload)
    },

    createUploadFormData(data) {
      const formData = new FormData()

      formData.append('file', data.file)
      formData.append('socketId', Socket.getSocketId())
      formData.append('accessCode', data.accessCode)

      return formData
    },

    handleUploadCroppedImage(formData) {
      return this.$http.post(Config.api.uploadImage, formData)
    },

    async handleResizeImage(slideOptionId, imageUrl) {
      const croppedImage = imageUrl.split('?')[0]
      const extension = croppedImage.split('.').pop()
      return this.$http.post(Config.api.uploadSlideOptionImage, {
        extension,
        slideOptionId,
        image: imageUrl,
        presentationId: this.presentation.id,
        socketId: Socket.getSocketId(),
        accessCode: this.presentation.uniqueAccessCode,
      })
    },

    async handleUploadLinkImage(imageUrl) {
      return this.$http.post(Config.api.uploadLink, {
        image: imageUrl,
      })
    },

    async handleUpdateGifInBackground(data) {
      const { imageUrl } = data
      const blob = await fetch(imageUrl).then((r) => r.blob())
      try {
        this.updateSlideOptionImageLocally({
          blob,
          cropperData: null,
          extension: 'gif',
          originImage: null,
          slideOption: this.slideOption,
        })
        this.isShowUploadModal = false
      } catch (e) {
        // do nothing
      } finally {
        const uploadLinkResponse = await this.handleUploadLinkImage(imageUrl)
        // resize
        const s3ImageUrl = uploadLinkResponse.body.file
        const resizedImage = await this.handleResizeImage(this.slideOption.id, s3ImageUrl)

        // update image url to slide object
        const payload = {
          image: null,
          imageCropped: resizedImage.body.origin,
          lastCropperData: null,
          presentationId: this.presentation.id,
          accessCode: this.presentation.uniqueAccessCode,
          socketId: Socket.getSocketId(),
        }

        // Update on API
        await SlideOptionApi.updateSlideOption(this.slideOption.id, payload)
        this.isShowUploadModal = false
      }
    },

    async handleUpdateInBackground(data, unfinishedSlideOptionId) {
      if (unfinishedSlideOptionId) {
        await this.increaseRetryTime(unfinishedSlideOptionId, Stores.slideOptionImageStore)
      } else {
        await this.addUploadTask(this.slideOption.id,
          this.presentation.id,
          data,
          Stores.slideOptionImageStore)
      }

      try {
        this.updateSlideOptionImageLocally({
          blob: data.blob,
          cropperData: data.cropperData,
          extension: 'jpg',
          originImage: data.originImageUrl,
          slideOption: this.slideOption,
        })
        this._closeModalCrop()
      } catch (e) {
        // do nothing
      } finally {
        const uploadFormData = this.createUploadFormData(data)

        const uploadRes = await this.handleUploadCroppedImage(uploadFormData)

        const imageUrl = uploadRes.body.file.location
        const resizedImage = await this.handleResizeImage(this.slideOption.id, imageUrl)

        // debugger

        const payload = {
          image: resizedImage.body.origin,
          imageCropped: resizedImage.body.origin,
          lastCropperData: data.cropperData,
          presentationId: this.presentation.id,
          accessCode: this.presentation.uniqueAccessCode,
          socketId: Socket.getSocketId(),
        }
        // Update slideoption model on API
        await SlideOptionApi.updateSlideOption(this.slideOption.id, payload)
        this._closeModalCrop()
        if (unfinishedSlideOptionId) {
          await this.removeUploadTask(unfinishedSlideOptionId, Stores.slideOptionImageStore)
        } else {
          await this.removeUploadTask(this.slideOption.id, Stores.slideOptionImageStore)
        }
      }
    },

    async _updateOption(params, id, cb) {
      try {
        params.socketId = Socket.getSocketId()
        params.presenting = this.$store.getters.isPresenting
        params.presentationId = this.presentation.id

        await SlideOptionApi.updateSlideOption(id, params)
        this.setSyncLoadingAction(2)
        if (cb) cb()
        EventBus.$emit('changeSlideOption')
      } catch (error) {
        this.setSyncLoadingAction(3)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.question-img-btn {
  .question-img-btn-box {
    padding: 14px 12px;
    height: 44px;
    border-radius: 0;
    .fa-image {
      font-size: 17px;
    }
    img {
      max-width: 200%;
      max-height: 30px;
    }
    .fa-pencil-alt {
      display: none;
    }
  }
  .btn-box-edit:hover {
    img {
      display: none;
    }
    .fa-pencil-alt {
      display: block;
    }
  }
}

.option-img-btn {
  position: relative;
}
.question-img-tooltip {
  position: absolute;
  right: 55px;
  top: -17px;
  z-index: 9;
  .tooltip-box {
    background-color: rgba(0, 0, 0, 0.9);
    height: 84px;
    width: 84px;
    border-radius: 5px;
    .btn {
      padding: 0.1rem 0.55rem;
      font-weight: bold;
    }
  }
  .tooltip-arrow {
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 6px solid rgba(0, 0, 0, 0.9);
    position: absolute;
    left: 84px;
    top: 40%;
  }
}
</style>
